import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { EMPTY_VALUE_PLACEHOLDER, formatDecimal, formatEuro, formatInteger } from "@shared";

import { SrvProceedingAssetSnapshotResponse } from "@/api";
import { useTranslation } from "@/translations";

import { KeyFigure } from "./components/key-figure/KeyFigure";

const TRANSLATION_BASE = "clients.client.debtSettlement.srvProceeding.assetSnapshot";
const EXPENSES_BASE = "clients.client.debtSettlement.assetInventory.expensesTab";

interface Props {
  /** null for a Verfahren started before snapshots existed. */
  snapshot: SrvProceedingAssetSnapshotResponse | null;
}

/** The Vermögensverzeichnis figures as they stood when the Verfahren was created; read-only, never refreshed. */
export function AssetSnapshotSection({ snapshot }: Props) {
  const { t, i18n } = useTranslation();

  const euro = (value: number | null) => (value === null ? EMPTY_VALUE_PLACEHOLDER : formatEuro(value, i18n.language));

  return (
    <Stack gap={3}>
      <Typography variant="h5">{t("clients.client.debtSettlement.srvProceeding.sections.assetSnapshot")}</Typography>
      {!snapshot ? (
        <Typography variant="body2" color="text.secondary">
          {t(`${TRANSLATION_BASE}.notTaken`)}
        </Typography>
      ) : !snapshot.isAvailable ? (
        <Typography variant="body2" color="text.secondary">
          {t(`${TRANSLATION_BASE}.notAvailable`)}
        </Typography>
      ) : (
        <>
          <Stack direction={{ xs: "column", md: "row" }} gap={2}>
            <KeyFigure label={t(`${TRANSLATION_BASE}.maintenanceObligationCount`)}>
              {snapshot.maintenanceObligationCount === null
                ? EMPTY_VALUE_PLACEHOLDER
                : formatInteger(snapshot.maintenanceObligationCount, i18n.language)}
            </KeyFigure>
            <KeyFigure label={t(`${TRANSLATION_BASE}.monthlyIncome`)}>{euro(snapshot.monthlyIncome)}</KeyFigure>
            <KeyFigure label={t(`${TRANSLATION_BASE}.seizableAmount`)}>{euro(snapshot.seizableAmount)}</KeyFigure>
          </Stack>
          <Table size="small" aria-label={t(`${TRANSLATION_BASE}.caption`)}>
            <TableHead>
              <TableRow>
                <TableCell>{t(`${TRANSLATION_BASE}.expensePosition`)}</TableCell>
                <TableCell align="right">{t(`${TRANSLATION_BASE}.expenseAmount`)}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {snapshot.maintenanceExpense && (
                <TableRow>
                  <TableCell component="th">
                    {t(`${EXPENSES_BASE}.table.maintenance`, { count: snapshot.maintenanceExpense.personCount })}
                  </TableCell>
                  <TableCell align="right">
                    {formatDecimal(snapshot.maintenanceExpense.amount, i18n.language)}
                  </TableCell>
                </TableRow>
              )}
              {snapshot.expenses.map((expense) => (
                <TableRow key={expense.position}>
                  <TableCell component="th">{t(`${EXPENSES_BASE}.positions.${expense.position}`)}</TableCell>
                  <TableCell align="right">{formatDecimal(expense.amount, i18n.language)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell component="th">
                  <Typography fontWeight="bold">{t(`${TRANSLATION_BASE}.totalExpenses`)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{formatDecimal(snapshot.totalExpenses, i18n.language)}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </>
      )}
    </Stack>
  );
}
